import { AutocompleteProps, Cell } from '@zeiss/beyond-online-react';
import { Autocomplete, Box, Grid } from '@zeiss/lib-components';
import { useDebounce } from '@zeiss/lib-helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getUserInformation, UserSearchProps, UserSearchResultItems } from './';
import { AutoCompleteLoading } from 'src/components/shared/AutoCompleteLoading';
import { DEBOUNCE_DELAY } from 'src/constants';
import { useSearchUsers } from 'src/hooks/api';

/**
 * Component supports searching users by name, email or phone.
 */
export const UserSearchResult = ({
  colSpan = [4, 6, 5],
  handleUserSelect,
  id = 'users',
  label,
  minSelectedUser = 0,
  restrictToZeissEmailDomain,
  selectedUsers,
  zIndexList = 1,
}: UserSearchProps) => {
  const { t } = useTranslation(['common']);

  const [helperText, setHelperText] = React.useState('');

  const [searchTerm, setSearchTerm] = React.useState('');

  const debouncedSearchParam = useDebounce(searchTerm, DEBOUNCE_DELAY);

  const { data, isPending } = useSearchUsers({
    restrictToZeissEmailDomain,
    searchQuery: debouncedSearchParam,
  });

  const options = React.useMemo(() => {
    let result: AutocompleteProps['items'] = [];

    if (data && data.items) {
      const selectedUserIds = selectedUsers.reduce(
        (acc: Record<string, boolean>, curr) => {
          acc[curr.id] = true;
          return acc;
        },
        {},
      );

      /**
       * Filter out the items that the user has already picked.
       * Have to be filtered also by email until BE fixes multiple emails
       */
      result = data.items
        .filter((item) => !selectedUserIds[item.id])
        .filter(
          (item) =>
            !selectedUsers.some(
              (selectedUser) => item.mail === selectedUser.mail,
            ),
        )
        .map((item) => {
          return {
            label: getUserInformation({ t, user: item }),
            value: item.id,
          };
        });

      /**
       * Filter out duplications from the result by email
       * TODO remove this once BE is fixed
       */
      result = result.filter(
        (obj, index, arr) =>
          arr.findIndex((item) => item.label === obj.label) === index,
      );
    }

    return result;
  }, [data, selectedUsers, t]);

  const handleChange = (value: string) => {
    setSearchTerm(value);
  };

  /**
   * Beyond Online reports typing through `onChange` and committed selection
   * through `onSelectionChange`. Handle the actual selection here so exact-ID
   * input still works when the selected option key equals the current input.
   */
  const handleSelectionChange = React.useCallback(
    (selectedKey: React.Key | null) => {
      if (typeof selectedKey !== 'string' || !data?.items) {
        return;
      }

      const selectedItem = data.items.find(({ id }) => id === selectedKey);
      if (selectedItem) {
        setSearchTerm('');
        handleUserSelect([...selectedUsers, selectedItem]);
      }
    },
    [data, handleUserSelect, selectedUsers],
  );

  React.useEffect(() => {
    if (!isPending) {
      if (options.length > 0) {
        setHelperText(t('common:input.searchHelperText'));
      }
      if (options.length === 0) {
        setHelperText(
          t('common:emptyState.searchNoX', {
            x: t('common:glossary.user'),
          }),
        );
      }
    }
    if (searchTerm.length === 0) {
      setHelperText('');
    }
  }, [isPending, options, searchTerm]);

  const deselect = (id: string) => {
    if (selectedUsers.length > minSelectedUser) {
      handleUserSelect([...selectedUsers.filter((user) => user.id !== id)]);
    }
  };

  /**
   * NOTE: Beyond Online React Workaround
   * The current (v4.31.0) implementation of the `AutoComplete` component only
   * shows the loading state if the `loading` prop is set to true **and** there
   * is no text in the input field.
   * This workaround uses the `emptyState` prop to show a loading spinner
   * when the user is typing in the input field and the request is pending.
   * This way we can show a loading spinner while the user is typing.
   */
  const loading = !!searchTerm && isPending;

  return (
    <>
      <Box>
        <Grid>
          <Cell.Basic colSpan={colSpan}>
            <Autocomplete
              autoComplete="off"
              data-testid="UserSearchAutoComplete"
              emptyState={loading ? <AutoCompleteLoading /> : undefined}
              helperText={helperText}
              id={id}
              inputValue={searchTerm}
              items={options}
              label={label}
              loading={!!debouncedSearchParam && isPending}
              onChange={handleChange}
              onSelectionChange={handleSelectionChange}
              placeholder={t('common:input.searchPlaceholder', {
                x: `${t('common:glossary.email')}, ${t('common:phone')}, ${t('common:userId')}`,
              }).replace('id', 'ID')}
              zIndex={1}
              zIndexList={zIndexList}
            />
          </Cell.Basic>
        </Grid>
      </Box>

      {selectedUsers.length ? (
        <UserSearchResultItems
          disabled={selectedUsers.length <= minSelectedUser}
          onDeselect={deselect}
          selectedUsers={selectedUsers}
        />
      ) : null}
    </>
  );
};
